import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";

interface ProductItem {
  id: string;
  productId: number;
  quantity: number;
  unitPrice: number;
  laborHours: number;
  markup: number;
  totalCost: number;
}

interface LaborRateEntry {
  role: string;
  hourlyRate: number;
  estimatedHours: number;
  totalCost: number;
}

interface MaterialCostEntry {
  materialId: number;
  quantity: number;
  unitCost: number;
  totalCost: number;
}

interface ProposalData {
  customerRfqId: number | null;
  projectName: string;
  projectLocation: string;
  contactPersonId: number | null;
  estimatedStartDate: Date | null;
  estimatedDuration: number; // in weeks
  products: ProductItem[];
  laborRates: LaborRateEntry[];
  materialCosts: MaterialCostEntry[];
  overhead: number; // percentage
  profit: number; // percentage
  salesTax: number; // percentage
  notes: string;
  terms: string;
  validUntil: Date | null;
}

class ProjectBidProposalWizardStore {
  rootStore: RootStore;

  // Observables
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  proposalData: ProposalData = {
    customerRfqId: null,
    projectName: "",
    projectLocation: "",
    contactPersonId: null,
    estimatedStartDate: null,
    estimatedDuration: 12,
    products: [],
    laborRates: [
      { role: "Project Manager", hourlyRate: 75, estimatedHours: 40, totalCost: 0 },
      { role: "Engineer", hourlyRate: 65, estimatedHours: 80, totalCost: 0 },
      { role: "Production Supervisor", hourlyRate: 55, estimatedHours: 160, totalCost: 0 },
      { role: "Skilled Labor", hourlyRate: 35, estimatedHours: 320, totalCost: 0 },
      { role: "General Labor", hourlyRate: 25, estimatedHours: 240, totalCost: 0 },
    ],
    materialCosts: [],
    overhead: 15,
    profit: 20,
    salesTax: 8.5,
    notes: "",
    terms: "Net 30 days",
    validUntil: null,
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
    this.calculateLaborCosts();
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    this.activeStep++;
  };

  prevStep = () => {
    this.activeStep--;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  updateProposalData = <K extends keyof ProposalData>(
    field: K,
    value: ProposalData[K]
  ) => {
    this.proposalData[field] = value;
  };

  selectCustomerRfq = (rfqId: number) => {
    // In a real implementation, this would fetch RFQ details
    this.proposalData.customerRfqId = rfqId;
  };

  addProduct = () => {
    const newProduct: ProductItem = {
      id: Date.now().toString(),
      productId: 0,
      quantity: 1,
      unitPrice: 0,
      laborHours: 0,
      markup: 25,
      totalCost: 0,
    };

    this.proposalData.products.push(newProduct);
  };

  removeProduct = (id: string) => {
    this.proposalData.products = this.proposalData.products.filter(p => p.id !== id);
  };

  updateProduct = (id: string, field: keyof ProductItem, value: any) => {
    const product = this.proposalData.products.find(p => p.id === id);
    if (product) {
      (product as any)[field] = value;
      this.calculateProductCost(product);
    }
  };

  calculateProductCost = (product: ProductItem) => {
    const basePrice = product.quantity * product.unitPrice;
    const laborCost = product.laborHours * 35; // average labor rate
    const subtotal = basePrice + laborCost;
    product.totalCost = subtotal * (1 + product.markup / 100);
  };

  updateLaborRate = (index: number, field: keyof LaborRateEntry, value: any) => {
    this.proposalData.laborRates[index] = {
      ...this.proposalData.laborRates[index],
      [field]: value,
    };
    this.calculateLaborCosts();
  };

  calculateLaborCosts = () => {
    this.proposalData.laborRates.forEach(rate => {
      rate.totalCost = rate.hourlyRate * rate.estimatedHours;
    });
  };

  addMaterial = () => {
    this.proposalData.materialCosts.push({
      materialId: 0,
      quantity: 1,
      unitCost: 0,
      totalCost: 0,
    });
  };

  removeMaterial = (index: number) => {
    this.proposalData.materialCosts.splice(index, 1);
  };

  updateMaterial = (index: number, field: keyof MaterialCostEntry, value: any) => {
    this.proposalData.materialCosts[index] = {
      ...this.proposalData.materialCosts[index],
      [field]: value,
    };
    this.calculateMaterialCost(index);
  };

  calculateMaterialCost = (index: number) => {
    const material = this.proposalData.materialCosts[index];
    material.totalCost = material.quantity * material.unitCost;
  };

  // Async actions
  generateProposal = async () => {
    this.loading = true;
    this.error = null;

    try {
      // In a real implementation, this would create quotes/bids via API
      const quotesStore = this.rootStore.quotesStore as ServiceStore;

      const proposalData = {
        accountId: null,
        contactId: this.proposalData.contactPersonId,
        projectName: this.proposalData.projectName,
        location: this.proposalData.projectLocation,
        estimatedStartDate: this.proposalData.estimatedStartDate,
        estimatedDuration: this.proposalData.estimatedDuration,
        subtotal: this.subtotal,
        totalLabor: this.totalLaborCost,
        totalMaterials: this.totalMaterialCost,
        overhead: this.overheadAmount,
        profit: this.profitAmount,
        salesTax: this.salesTaxAmount,
        total: this.grandTotal,
        notes: this.proposalData.notes,
        terms: this.proposalData.terms,
        validUntil: this.proposalData.validUntil,
        status: "Draft",
      };

      // Simulated API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      runInAction(() => {
        this.success = "Proposal generated successfully!";
        this.nextStep();
      });

      return proposalData;
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to generate proposal";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.proposalData = {
      customerRfqId: null,
      projectName: "",
      projectLocation: "",
      contactPersonId: null,
      estimatedStartDate: null,
      estimatedDuration: 12,
      products: [],
      laborRates: [
        { role: "Project Manager", hourlyRate: 75, estimatedHours: 40, totalCost: 0 },
        { role: "Engineer", hourlyRate: 65, estimatedHours: 80, totalCost: 0 },
        { role: "Production Supervisor", hourlyRate: 55, estimatedHours: 160, totalCost: 0 },
        { role: "Skilled Labor", hourlyRate: 35, estimatedHours: 320, totalCost: 0 },
        { role: "General Labor", hourlyRate: 25, estimatedHours: 240, totalCost: 0 },
      ],
      materialCosts: [],
      overhead: 15,
      profit: 20,
      salesTax: 8.5,
      notes: "",
      terms: "Net 30 days",
      validUntil: null,
    };
    this.calculateLaborCosts();
  };

  // Computed values
  get canProceedFromStep1() {
    return !!this.proposalData.projectName;
  }

  get canProceedFromStep2() {
    return this.proposalData.products.length > 0;
  }

  get canProceedFromStep3() {
    return this.proposalData.laborRates.every(rate => rate.hourlyRate > 0);
  }

  get canGenerateProposal() {
    return this.canProceedFromStep1 && this.canProceedFromStep2 && this.canProceedFromStep3;
  }

  get customerRfqs() {
    // Mock data for now
    return [
      { id: 1, rfqNumber: "RFQ-001", projectName: "Office Building" },
      { id: 2, rfqNumber: "RFQ-002", projectName: "Warehouse Complex" },
    ];
  }

  get products() {
    const productsStore = this.rootStore.productsStore as ServiceStore;
    return productsStore?.entities || [];
  }

  get materials() {
    const materialsStore = this.rootStore.materialsStore as ServiceStore;
    return materialsStore?.entities || [];
  }

  get accounts() {
    const accountsStore = this.rootStore.accountsStore as ServiceStore;
    return accountsStore?.entities || [];
  }

  get contacts() {
    const contactsStore = this.rootStore.contactsStore as ServiceStore;
    return contactsStore?.entities || [];
  }

  get selectedRfq() {
    if (!this.proposalData.customerRfqId) return null;
    return this.customerRfqs.find((r: any) => r.id === this.proposalData.customerRfqId);
  }

  get selectedContact() {
    if (!this.proposalData.contactPersonId) return null;
    return this.contacts.find((c: any) => c.id === this.proposalData.contactPersonId);
  }

  get totalProductCost() {
    return this.proposalData.products.reduce((sum, product) => sum + product.totalCost, 0);
  }

  get totalLaborCost() {
    return this.proposalData.laborRates.reduce((sum, rate) => sum + rate.totalCost, 0);
  }

  get totalMaterialCost() {
    return this.proposalData.materialCosts.reduce((sum, material) => sum + material.totalCost, 0);
  }

  get subtotal() {
    return this.totalProductCost + this.totalLaborCost + this.totalMaterialCost;
  }

  get overheadAmount() {
    return this.subtotal * (this.proposalData.overhead / 100);
  }

  get profitAmount() {
    return (this.subtotal + this.overheadAmount) * (this.proposalData.profit / 100);
  }

  get subtotalWithProfitAndOverhead() {
    return this.subtotal + this.overheadAmount + this.profitAmount;
  }

  get salesTaxAmount() {
    return this.subtotalWithProfitAndOverhead * (this.proposalData.salesTax / 100);
  }

  get grandTotal() {
    return this.subtotalWithProfitAndOverhead + this.salesTaxAmount;
  }
}

export default ProjectBidProposalWizardStore;